import { createFlagsmithStorage } from '@/app-shell/flags/flagsmith-storage';
import { FlagsmithClientProvider } from '@openfeature/flagsmith-client-provider';
import { GeneralError, ProviderEvents } from '@openfeature/web-sdk';
import { isEqual } from 'lodash-es';

const FLAGSMITH_INIT_TIMEOUT_MS = 8_000;
// Wider than the boot bound: a slow-but-healthy identify during login (proxy
// cold start, Flagsmith deploy) is the likelier false positive, and
// evaluation keeps serving — code defaults at ERROR — while it settles.
const FLAGSMITH_CONTEXT_CHANGE_TIMEOUT_MS = 20_000;

/**
 * Bound a Flagsmith `initialize()` that may never settle: flagsmith-js has no
 * request timeout, and a black-holed connection parks the OpenFeature domain
 * at NOT_READY — a status readiness gates never treat as resolved. (A fetch
 * that *errors* is different: flagsmith swallows it and init resolves.)
 * Rejecting instead lands the domain at ERROR — a settled state consumers
 * fall back from onto code defaults, and one the client-SDK self-report
 * treats as diagnosable.
 *
 * Recovery from ERROR is two-track. VALUES: the still-pending flagsmith fetch
 * completing fires the SDK's onChange, so real flags land and hooks
 * re-render. STATUS: the next successful `setContext` flips the domain back
 * to READY (the SDK's context-change handler sets READY on success, and
 * `OpenFeatureContextSync` re-fires setContext on login and every trait
 * change). Provider-emitted events alone do NOT promote status out of ERROR
 * — the SDK forwards them to listeners without mutating its tracked status.
 */
export function withInitTimeout(
  init: Promise<void>,
  timeoutMs: number,
  operation = 'flagsmith init'
): Promise<void> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  // Observe a late rejection after the bound wins so it is not unhandled.
  init.catch(() => undefined);
  return Promise.race([
    init,
    new Promise<never>((_resolve, reject) => {
      timer = setTimeout(
        () => reject(new GeneralError(`${operation} did not settle in ${timeoutMs}ms`)),
        timeoutMs
      );
    }),
  ]).finally(() => clearTimeout(timer));
}

export class BoundedInitFlagsmithProvider extends FlagsmithClientProvider {
  override initialize(
    context?: Parameters<FlagsmithClientProvider['initialize']>[0]
  ): Promise<void> {
    return withInitTimeout(super.initialize(context), FLAGSMITH_INIT_TIMEOUT_MS);
  }

  /**
   * The base delegates `onContextChange` to `this.initialize`, which would
   * inherit the 8s boot bound. Call the UNBOUNDED base initialize under the
   * wider context-change bound instead, replicating the base's Stale
   * emission.
   */
  override onContextChange(
    oldContext: Parameters<FlagsmithClientProvider['onContextChange']>[0],
    newContext: Parameters<FlagsmithClientProvider['onContextChange']>[1]
  ): Promise<void> {
    if (isEqual(oldContext, newContext)) return Promise.resolve();
    this.events.emit(ProviderEvents.Stale, { message: 'Context Changed' });
    return withInitTimeout(
      super.initialize(newContext),
      FLAGSMITH_CONTEXT_CHANGE_TIMEOUT_MS,
      'flagsmith context change'
    );
  }
}

export const createFlagsmithProvider = (
  environmentID: string
): BoundedInitFlagsmithProvider =>
  new BoundedInitFlagsmithProvider({
    environmentID,
    api: 'https://sys.heidihealth.com/fs/api/v1/',
    cacheFlags: true,
    cacheOptions: {
      skipAPI: false,
    },
    AsyncStorage: createFlagsmithStorage(),
  });
